/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Description,
  Dropdown,
  Input,
  Label,
  Message,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { ServiceLevelSwitch } from "~/components/BillingRules/ServiceLevelSwitch";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import {
  CapacityBasedFeeTypeEnum,
  capacityBaseFeeOptions,
} from "~/models/assignmentRule";
import { Environment } from "~/models/environment";
import {
  useAxios,
  generateActionSuccessText,
  checkIsValueWithinRange,
  toFraction,
  toPercentage,
} from "~/tools";
import { Level } from "~/views/globalSettings/types";

type EnvServiceLevelProps = {
  environment: Environment;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => Promise<void>;
  isEditMode: boolean;
};

type Values = {
  capacityBasedPercentageFee: string;
  baseFeeOption: CapacityBasedFeeTypeEnum;
  capacityBasedFeeType: CapacityBasedFeeTypeEnum;
};

const initialStateLevel = {
  capacityBasedFee: {
    minPercentage: "",
    maxPercentage: "",
    defaultPercentage: "",
  },
  id: "",
  index: 0,
  name: "",
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function EnvServiceLevel({
  environment,
  setIsEditMode,
  refetch,
  isEditMode,
}: EnvServiceLevelProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmittingLoading, setIsSubmittingLoading] = React.useState(false);
  const [serviceLevels, setServiceLevels] = React.useState<Array<Level>>([]);
  const [serviceLevelId, setServiceLevelId] = React.useState<string>("");

  const [type, setType] = React.useState("");

  const [level, setLevel] = React.useState<Level>(initialStateLevel);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { register, setValue, errors, handleSubmit, watch } = useForm();

  const watcher = watch();

  const fetchServiceLevels = React.useCallback(async () => {
    try {
      const { data } = await Axios().get("/servicelevels");
      setServiceLevels(data?.serviceLevels);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, showError]);

  const levelName = environment?.attachedServiceLevel?.serviceLevel.name;

  React.useEffect(() => {
    const findLevel = serviceLevels?.find(
      (item: Level) => item.id === serviceLevelId
    );
    findLevel && setLevel(findLevel);
  }, [serviceLevelId, serviceLevels]);

  React.useEffect(() => {
    isEditMode && fetchServiceLevels();
  }, [fetchServiceLevels, isEditMode]);

  const capacityBasedFeeDefault = toPercentage(
    level?.capacityBasedFee?.defaultPercentage
  );
  const capacityBasedFeeMin = toPercentage(
    level?.capacityBasedFee?.minPercentage
  );

  const capacityBasedFeeMax = toPercentage(
    level?.capacityBasedFee?.maxPercentage
  );

  const effectiveFee = toPercentage(
    environment?.attachedServiceLevel?.effectiveCapacityBasedPercentageFee
  );

  React.useEffect(() => {
    setValue(
      "baseFeeOption",
      environment?.attachedServiceLevel?.capacityBasedFeeType
    );
  }, [
    environment?.attachedServiceLevel?.capacityBasedFeeType,
    setValue,
    serviceLevelId,
  ]);

  const checkValue = (switchValue: boolean, formValue: string) =>
    switchValue ? null : formValue;

  const onSubmit = async (values: Values) => {
    setIsSubmittingLoading(true);
    const fraction = toFraction(values.capacityBasedPercentageFee).toString();
    const organizeValues = {
      ...environment,
      attachedServiceLevel: {
        capacityBasedFeeType: values.baseFeeOption,
        capacityBasedPercentageFee: checkValue(
          values.baseFeeOption === CapacityBasedFeeTypeEnum.DEFAULT_TYPE ||
            values.baseFeeOption ===
              CapacityBasedFeeTypeEnum.INCLUDED_IN_BASE_FEE_TYPE,
          fraction
        ),
        serviceLevelId,
      },
    };
    try {
      await Axios().put(`/environments/${environment.id}`, organizeValues);
      showSuccess(generateActionSuccessText(`Service Level`)()(`updated`)());
    } catch (e) {
      showError(e.response);
    } finally {
      clean();
    }
  };

  const id = environment?.attachedServiceLevel?.serviceLevel?.id;

  React.useEffect(() => {
    register({
      name: "baseFeeOption",
    });
  }, [register]);

  React.useEffect(() => {
    id && setServiceLevelId(id);
  }, [id, setServiceLevelId]);

  const clean = () => {
    refetch();
    setIsSubmittingLoading(false);
    setServiceLevelId("");
    setIsEditMode(false);
  };

  return (
    <LoaderWrap loading={isLoading}>
      {serviceLevels?.length === 0 && (
        <Message css={{ marginBottom: "1rem" }} status="danger">
          No service levels defined
        </Message>
      )}
      <ServiceLevelSwitch
        name={levelName}
        setType={setType}
        type={type}
        serviceLevels={serviceLevels}
        setServiceLevelId={setServiceLevelId}
      />
      <Spacer height="1rem" />
      {serviceLevelId && (
        <>
          <Label name="Capacity Based Fee" />
          <Spacer height="0.5rem" />
          <FlexContainer css={{ width: "15rem" }}>
            <Dropdown
              onChange={(option: string) => {
                setValue("baseFeeOption", option);
              }}
              value={watcher.baseFeeOption}
              name="Choose type"
              options={capacityBaseFeeOptions?.map(
                ({ value, label }) =>
                  ({
                    value,
                    label,
                  } || [])
              )}
            />
          </FlexContainer>
          {watcher.baseFeeOption === CapacityBasedFeeTypeEnum.DEFAULT_TYPE && (
            <FlexContainer css={{ width: "15rem", marginTop: "1rem" }}>
              <Text size="md" tag="span">
                Default Value:
              </Text>
              <Text size="md" ml="1rem" tag="span" color={theme.colors.success}>
                {capacityBasedFeeDefault} %
              </Text>
            </FlexContainer>
          )}
          <Fieldset>
            {watcher.baseFeeOption === CapacityBasedFeeTypeEnum.CUSTOM_TYPE && (
              <>
                <Text weight="medium" size="sm" css={{ marginTop: "1rem" }}>
                  Value has to be within range Min: {capacityBasedFeeMin}
                  {"%"} Max: {capacityBasedFeeMax}
                  {"%"}
                </Text>
                <FlexContainer
                  css={{ width: "15rem", marginTop: "1rem" }}
                  key={effectiveFee}
                >
                  <Input
                    type="number"
                    name="capacityBasedPercentageFee"
                    defaultValue={effectiveFee}
                    placeholder=""
                    status={errors.capacityBasedPercentageFee && "danger"}
                    ref={register({
                      required: true,
                      validate: (value: string) =>
                        checkIsValueWithinRange(
                          value,
                          capacityBasedFeeMin,
                          capacityBasedFeeMax
                        ),
                    })}
                  />
                  <Text css={{ marginLeft: "1rem" }} tag="div">
                    %
                  </Text>
                </FlexContainer>
              </>
            )}
            {errors.capacityBasedPercentageFee && (
              <Description>
                Value has to be within range {capacityBasedFeeMin} to{" "}
                {capacityBasedFeeMax}
              </Description>
            )}
          </Fieldset>
        </>
      )}
      <FlexContainer>
        <Button
          css={{ marginRight: "auto" }}
          initialState={isSubmittingLoading ? "loading" : ""}
          disabled={!serviceLevelId || !watcher.baseFeeOption}
          onClick={handleSubmit(onSubmit)}
        >
          Apply
        </Button>
        <Button
          severity="medium"
          type="button"
          onClick={() => {
            clean();
          }}
        >
          Cancel
        </Button>
      </FlexContainer>
    </LoaderWrap>
  );
}

export { EnvServiceLevel };
